import { isEqual, uniqBy } from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";

import { Box } from "@mui/material";

import { threadsFiltersState } from "@/state/threads";

import { ThreadList } from "./ThreadList";
// import Filters from "./filters";
import { GenAiContext } from "@/client/context";
import { useChatMessages } from "@/client/useChatMessages";
import { accessTokenState, threadHistoryState } from "@/client/state";
import { IThreadFilters } from "@/client/api";

const BATCH_SIZE = 20;

let _scrollTop = 0;

export function ThreadHistory() {
  const filters = useRecoilValue(threadsFiltersState);
  const ref = useRef<HTMLDivElement>(null);
  const [prevFilters, setPrevFilters] = useState<IThreadFilters>(filters);

  const filtersHasChanged = !isEqual(prevFilters, filters);
  const [threadHistory, setThreadHistory] = useRecoilState(threadHistoryState);
  const accessToken = useRecoilValue(accessTokenState);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const apiClient = useContext(GenAiContext);
  const { firstInteraction, messages, threadId } = useChatMessages();
  const navigate = useNavigate();

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = _scrollTop;
    }
  }, []);

  useEffect(() => {
    if (!firstInteraction) {
      return;
    }

    // distinguish between the first interaction containing the word "resume"
    // and the actual resume message
    const isActualResume =
      firstInteraction === "resume" &&
      messages.at(0)?.content.toLowerCase() !== "resume";

    if (isActualResume) {
      return;
    }

    fetchThreads(undefined, true).then(() => {
      const currentPage = new URL(window.location.href);
      if (threadId && currentPage.pathname === "/") {
        navigate(`/thread/${threadId}`);
      }
    });
  }, [firstInteraction]);

  const handleScroll = () => {
    if (!ref.current) return;

    const { scrollHeight, clientHeight, scrollTop } = ref.current;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 10;

    //We save the scroll top in order to scroll to the element when the page is changing.
    _scrollTop = scrollTop;

    setShouldLoadMore(atBottom);
  };

  const fetchThreads = async (
    cursor?: string | number,
    isLoadingMore?: boolean
  ) => {
    try {
      if (cursor || isLoadingMore) {
        setIsLoadingMore(true);
      } else {
        setIsFetching(true);
      }

      const {
        data: { threads: data, pageInfo },
      } = await apiClient.listThreads(
        { first: BATCH_SIZE, cursor },
        filters,
        accessToken
      );

      setError(undefined);

      // Prevent threads to be duplicated
      const allThreads = uniqBy(
        // We should only concatenate threads when we have a cursor indicating that we have loaded more items.
        cursor ? threadHistory?.threads?.concat(data) : data,
        "thread_id"
      );

      if (allThreads) {
        setThreadHistory((prev) => ({
          ...prev,
          pageInfo: pageInfo,
          threads: allThreads,
        }));
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message || "Failed to load history");
      }
    } finally {
      setShouldLoadMore(false);
      setIsLoadingMore(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const shouldFetchInitial =
      accessToken && !isFetching && !threadHistory?.threads && !error;

    const shouldRefetchForFilters = filtersHasChanged && !isFetching;

    if (shouldFetchInitial || shouldRefetchForFilters) {
      fetchThreads();
      if (filtersHasChanged) {
        setPrevFilters(filters);
      }
    }
  }, [
    accessToken,
    isFetching,
    threadHistory,
    error,
    filtersHasChanged,
    filters,
  ]);

  useEffect(() => {
    if (threadHistory?.pageInfo) {
      const { hasNextPage, endCursor } = threadHistory.pageInfo;

      if (shouldLoadMore && !isLoadingMore && hasNextPage && endCursor) {
        fetchThreads(endCursor);
      }
    }
  }, [threadHistory, shouldLoadMore, isLoadingMore]);

  return (
    <>
      {/* <Filters /> */}
      {threadHistory ? (
        <Box
          sx={{ flexGrow: 1, overflow: "auto" }}
          onScroll={handleScroll}
          ref={ref}
        >
          <ThreadList
            threadHistory={threadHistory}
            error={error}
            isFetching={isFetching}
            isLoadingMore={isLoadingMore}
            fetchThreads={fetchThreads}
          />
        </Box>
      ) : null}
    </>
  );
}
